import React, { useState } from "react";

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const formatEditableValue = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  return String(numericValue);
};

const AttendeeValueCard = ({
  attendeeValue,
  canOverride,
  estimate,
  isSaving,
  onSaveOverride,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const [draftReason, setDraftReason] = useState("");
  const [formError, setFormError] = useState("");

  const effectiveValue = attendeeValue?.effective_value || 0;
  const latestOverride = attendeeValue?.latest_override;
  const showEstimateReasoning =
    attendeeValue?.source === "ai_estimate" && Boolean(estimate?.report);

  const handleEditOpen = () => {
    setFormError("");
    setDraftValue(formatEditableValue(effectiveValue));
    setDraftReason(latestOverride?.reason || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormError("");
    setDraftValue("");
    setDraftReason("");
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const parsedValue = Number(draftValue);
    const trimmedReason = draftReason.trim();

    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      setFormError("Enter an attendee value greater than 0.");
      return;
    }

    if (!trimmedReason) {
      setFormError("Add a short reason so your team knows why this changed.");
      return;
    }

    try {
      setFormError("");
      await onSaveOverride({ override_value: parsedValue, reason: trimmedReason });
      handleCancel();
    } catch (error) {
      setFormError(error?.response?.data?.error || "Failed to save attendee value.");
    }
  };

  return (
    <article className="event-analytics-preview__attendee-card">
      <div className="event-analytics-preview__attendee-hero">
        <p className="event-analytics-preview__attendee-value-label">Current attendee value</p>

        <div className="event-analytics-preview__attendee-value-row">
          <span className="event-analytics-preview__attendee-value">
            {formatCurrency(effectiveValue)}
          </span>
          <span className="event-analytics-preview__attendee-unit">per visitor</span>
        </div>

        <div className="event-analytics-preview__attendee-source-panel">
          <p className="event-analytics-preview__attendee-source-note">
            {attendeeValue?.source_note}
          </p>
        </div>
      </div>

      {showEstimateReasoning && (
        <div className="event-analytics-preview__attendee-reasoning">
          <button
            className="event-analytics-preview__attendee-toggle"
            onClick={() => setShowReasoning((open) => !open)}
            type="button"
          >
            {showReasoning ? "Hide" : "Show"} AI reasoning
          </button>

          {showReasoning && (
            <div className="event-analytics-preview__attendee-reasoning-body">
              <p>{estimate.report}</p>
            </div>
          )}
        </div>
      )}

      {canOverride && !isEditing && (
        <div className="event-analytics-preview__attendee-actions">
          <button
            className="event-analytics-preview__attendee-edit-button"
            onClick={handleEditOpen}
            type="button"
          >
            Adjust attendee value
          </button>
        </div>
      )}

      {canOverride && isEditing && (
        <form className="event-analytics-preview__attendee-form" onSubmit={handleSave}>
          <label className="event-analytics-preview__attendee-field">
            <span className="event-analytics-preview__attendee-field-label">Attendee value</span>
            <input
              className="event-analytics-preview__attendee-input"
              min="0.01"
              onChange={(event) => setDraftValue(event.target.value)}
              placeholder={formatEditableValue(effectiveValue)}
              step="0.01"
              type="number"
              value={draftValue}
            />
          </label>

          <label className="event-analytics-preview__attendee-field">
            <span className="event-analytics-preview__attendee-field-label">Reason</span>
            <textarea
              className="event-analytics-preview__attendee-textarea"
              onChange={(event) => setDraftReason(event.target.value)}
              placeholder="Explain why this value better reflects the event."
              rows="3"
              value={draftReason}
            />
          </label>

          {formError && <p className="event-analytics-preview__attendee-form-error">{formError}</p>}

          <div className="event-analytics-preview__attendee-form-actions">
            <button
              className="event-analytics-preview__attendee-save-button"
              disabled={isSaving}
              type="submit"
            >
              {isSaving ? "Saving..." : "Save override"}
            </button>

            <button
              className="event-analytics-preview__attendee-cancel-button"
              onClick={handleCancel}
              type="button"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </article>
  );
};

export default AttendeeValueCard;
